import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const authVersion = useRef(0);
  const authRequest = useRef(null);

  const invalidateAuthRequests = useCallback(() => {
    authVersion.current += 1;
    authRequest.current?.abort();
    authRequest.current = null;
  }, []);

  const updateUser = useCallback((nextUser) => {
    invalidateAuthRequests();
    setUser(nextUser);
    setLoading(false);
  }, [invalidateAuthRequests]);

  const checkAuth = useCallback(async () => {
    authRequest.current?.abort();
    const controller = new AbortController();
    authRequest.current = controller;
    const requestVersion = authVersion.current;
    try {
      const { data } = await api.get("/auth/me", { signal: controller.signal });
      if (requestVersion === authVersion.current) setUser(data);
    } catch {
      if (requestVersion === authVersion.current) setUser(null);
    } finally {
      if (requestVersion === authVersion.current) setLoading(false);
      if (authRequest.current === controller) authRequest.current = null;
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    invalidateAuthRequests();
    setUser(null);
    setLoading(false);
    await api.post("/auth/logout");
  }, [invalidateAuthRequests]);
  const value = useMemo(() => ({ user, setUser: updateUser, loading, checkAuth, logout }), [user, loading, checkAuth, updateUser, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
